import type { Role } from "../../../../shared/types";
import type { IconName } from "../../../components/ui/Icon";
import { needRole } from "../../../context/AuthContext";
import type { Home, HomeGoal, HomeProduct } from "../../../lib/ccApi";
import { needText } from "../text";

export type HomeNeed = Home["needs"][number];

/** One thing that waits for this person: a decision, a plan to review, or a goal getting ready. */
export interface Waiting {
  key: string;
  kind: "need" | "plan" | "ready";
  need: HomeNeed | null;
  goal: HomeGoal;
  product: HomeProduct;
  icon: IconName;
  title: string;
  /** One line about it, under its title. */
  line: string;
  /** The page that holds it on a computer. */
  to: string;
}

const goalPath = (id: string) => `/goals/${encodeURIComponent(id)}`;

/**
 * Everything that waits for this person, newest decisions first, then plans to review and goals getting ready;
 * one product with `only`. Only what their role may do: a plan and getting ready are an Editor's, each decision its kind's.
 */
export function waitingRows(home: Home, can: (min: Role) => boolean, only?: string): Waiting[] {
  const goals = new Map(home.products.filter((p) => !only || p.id === only).flatMap((p) => p.goals.map((g) => [g.id, { g, p }] as const)));
  const rows: Waiting[] = [];
  for (const n of home.needs) {
    const at = goals.get(n.goalId);
    if (!at || !can(needRole(n))) continue;
    const t = needText(n);
    rows.push({ key: n.id, kind: "need", need: n, goal: at.g, product: at.p, icon: t.icon, title: t.title, line: t.line, to: `${goalPath(n.goalId)}?need=${encodeURIComponent(n.id)}` });
  }
  for (const { g, p } of can("editor") ? goals.values() : []) {
    if (g.state === "plan_ready")
      rows.push({
        key: `plan-${g.id}`,
        kind: "plan",
        need: null,
        goal: g,
        product: p,
        icon: "doc",
        title: g.version > 0 ? "Review the new plan" : "Review the first plan",
        line: "The plan is ready for you.",
        to: `${goalPath(g.id)}${g.version > 0 ? "/edit" : ""}`,
      });
    if (g.state === "getting_ready" && g.readyLeft) {
      const total = g.readyTotal ?? g.readyLeft;
      rows.push({
        key: `ready-${g.id}`,
        kind: "ready",
        need: null,
        goal: g,
        product: p,
        icon: "check",
        title: `Get ready: ${g.readyLeft} of ${total} left`,
        line: "Before the ads can go live",
        to: `${goalPath(g.id)}/ready`,
      });
    }
  }
  return rows;
}

export interface WaitingGroup {
  goal: HomeGoal;
  product: HomeProduct;
  rows: Waiting[];
}

/** The rows grouped by goal, each goal where its first row was. */
export function byGoal(rows: Waiting[]): WaitingGroup[] {
  const groups = new Map<string, WaitingGroup>();
  for (const r of rows) {
    const g = groups.get(r.goal.id);
    if (g) g.rows.push(r);
    else groups.set(r.goal.id, { goal: r.goal, product: r.product, rows: [r] });
  }
  return [...groups.values()];
}

/** The order a phone steps through them in: goal by goal, as Needs you lists them. */
export const inGoalOrder = (rows: Waiting[]): Waiting[] => byGoal(rows).flatMap((g) => g.rows);
